import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconButton, Typography } from '@mui/material';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import './InvestmentToolsSlide.scss';

/**
 * 「常見的投資工具比較」, an in-house replacement for the Piktochart of the same
 * name: the pictures and click-by-click animation of 投資方式回顧.pptx, with the
 * wording (and its en / zh translations) of the old Piktochart.
 *
 * The pptx holds one composite picture (slide.webp) with its words baked in.
 * Only the word-free crops of it are used — icons, arrows, the "?" badge — and
 * every word is real text from the locale files, so it can be translated. Each
 * part is a rect as a % of the 16:9 slide, the click that reveals it and its
 * entrance effect; `spid` is the pptx shape id where a part came from the pptx.
 * Array order is draw order: the "?" and red boxes cover the green arrows of
 * the earlier clicks, as in the slide.
 */

const IMG_DIR = 'img/investment-tools';

type Effect = 'fade' | 'wipe-up' | 'wipe-down' | 'wipe-right' | 'split-out' | 'ascend';

type Rect = [number, number, number, number];

interface BasePart {
  key: string;
  /** left, top, width, height — % of the slide. */
  rect: Rect;
  /** Click that reveals it; 0 = on the slide from the start. */
  step: number;
  effect?: Effect;
}

interface ImagePart extends BasePart {
  src: 'slide' | 'arrow-up' | 'arrow-down';
  /** srcRect left, top, right, bottom in 1/100000 — only crops of slide.webp have one. */
  crop?: [number, number, number, number];
}

type TextKind = 'title' | 'pill' | 'row' | 'word' | 'pct' | 'keep';

interface TextPart extends BasePart {
  kind: TextKind;
  /** Locale message ids, one per line. */
  messages?: string[];
  /** Same in every language. */
  literal?: string;
  /** Font size in 1/100 of the slide width; `en` overrides it for the longer English words. */
  size: number;
  enSize?: number;
}

type SlidePart = ImagePart | TextPart;

const isText = (part: SlidePart): part is TextPart => 'kind' in part;

const QUESTION_CROP: ImagePart['crop'] = [68646, 58321, 24011, 28069];

// Icon crops stop above the pptx's own baked-in labels, which are replaced by text pills.
const ICON_TOP = 22500;
const ICON_BOTTOM = 53600;

const ICON_Y = 18.6;
const ICON_H = 20.1;
const PILL: Pick<TextPart, 'kind' | 'size' | 'enSize'> = { kind: 'pill', size: 2.8, enSize: 2.2 };
const PILL_Y = 40.5;
const WORD: Pick<TextPart, 'kind' | 'size' | 'enSize'> = { kind: 'word', size: 3.4, enSize: 2.1 };
const WORD_Y = 53.5;

// `wipe-up` grows from the bottom edge (pptx "Wipe From Bottom"), `wipe-down` from the top.
const PARTS: SlidePart[] = [
  { key: 'title', kind: 'title', rect: [10, 1.5, 80, 12], messages: ['its-title'], size: 5, enSize: 3.6, step: 0 },
  {
    key: 'income',
    kind: 'row',
    rect: [0.8, 52.5, 11.2, 12],
    messages: ['its-income'],
    size: 3.2,
    enSize: 1.8,
    step: 0
  },
  {
    key: 'principal',
    kind: 'row',
    rect: [0.8, 71.5, 11.2, 17],
    messages: ['its-principal'],
    size: 3.2,
    enSize: 1.8,
    step: 0
  },

  // click 1 — 買樓, 買股
  {
    key: 'icon-property',
    src: 'slide',
    rect: [13.6, ICON_Y, 15.4, ICON_H],
    crop: [11996, ICON_TOP, 69768, ICON_BOTTOM],
    step: 1,
    effect: 'fade'
  },
  { key: 'pill-property', ...PILL, rect: [13.4, PILL_Y, 15.6, 8], messages: ['its-property'], step: 1, effect: 'fade' },
  {
    key: 'icon-stocks',
    src: 'slide',
    rect: [29.6, ICON_Y, 15.4, ICON_H],
    crop: [28860, ICON_TOP, 52904, ICON_BOTTOM],
    step: 1,
    effect: 'fade'
  },
  { key: 'pill-stocks', ...PILL, rect: [29.4, PILL_Y, 15.6, 8], messages: ['its-stocks'], step: 1, effect: 'fade' },
  // click 6 — 定期
  {
    key: 'icon-deposit',
    src: 'slide',
    rect: [45.6, ICON_Y, 15.4, ICON_H],
    crop: [45563, ICON_TOP, 36201, ICON_BOTTOM],
    step: 6,
    effect: 'fade'
  },
  { key: 'pill-deposit', ...PILL, rect: [45.4, PILL_Y, 15.6, 8], messages: ['its-deposit'], step: 6, effect: 'fade' },
  // click 9 — 比特幣
  {
    key: 'icon-bitcoin',
    src: 'slide',
    rect: [61.9, ICON_Y, 15.4, ICON_H],
    crop: [62749, ICON_TOP, 19015, ICON_BOTTOM],
    step: 9,
    effect: 'fade'
  },
  { key: 'pill-bitcoin', ...PILL, rect: [61.7, PILL_Y, 15.6, 8], messages: ['its-bitcoin'], step: 9, effect: 'fade' },
  // click 12 — 保單 rises in
  {
    key: 'icon-insurance',
    src: 'slide',
    rect: [78.6, 18, 15.4, 20.7],
    crop: [80420, 21800, 1344, ICON_BOTTOM],
    step: 12,
    effect: 'ascend'
  },
  {
    key: 'pill-insurance',
    ...PILL,
    rect: [78.3, PILL_Y, 15.6, 8],
    messages: ['its-insurance'],
    step: 12,
    effect: 'ascend'
  },

  // income row
  { key: 'rent', ...WORD, rect: [13.2, WORD_Y, 9.8, 10.5], messages: ['its-rent'], step: 2, effect: 'fade' },
  { key: 'dividend', ...WORD, rect: [30.1, WORD_Y, 9.8, 10.5], messages: ['its-dividend'], step: 2, effect: 'fade' },
  { key: 'interest', ...WORD, rect: [47.0, WORD_Y, 9.8, 10.5], messages: ['its-interest'], step: 7, effect: 'wipe-up' },
  { key: 'return', ...WORD, rect: [62.6, WORD_Y, 9.6, 10.5], messages: ['its-return'], step: 10, effect: 'wipe-up' },
  {
    key: 'question-bitcoin',
    src: 'slide',
    rect: [72.2, 54.6, 4.7, 8.7],
    crop: QUESTION_CROP,
    step: 10,
    effect: 'wipe-up'
  },
  {
    key: 'insurance-return',
    kind: 'pct',
    rect: [78.5, 52.5, 17, 12],
    literal: '4-6.5%',
    size: 4.2,
    step: 13,
    effect: 'wipe-up'
  },

  // the green "up" arrows of clicks 3 and 4, covered on click 5
  { key: 'principal-up-property', src: 'arrow-up', rect: [16.38, 72.04, 7.98, 15.28], step: 4, effect: 'wipe-up' },
  { key: 'principal-up-stocks', src: 'arrow-up', rect: [33.08, 72.03, 7.98, 15.28], step: 4, effect: 'wipe-up' },
  { key: 'income-up-rent', src: 'arrow-up', rect: [23.81, 55.73, 3.16, 6.05], step: 3, effect: 'wipe-up' },
  { key: 'income-up-dividend', src: 'arrow-up', rect: [40.74, 55.61, 3.16, 6.05], step: 3, effect: 'wipe-up' },
  { key: 'income-up-interest', src: 'arrow-up', rect: [57.69, 55.55, 3.16, 6.05], step: 7, effect: 'wipe-up' },
  {
    key: 'principal-flat',
    src: 'slide',
    rect: [49.09, 72.95, 12.66, 13.44],
    crop: [49055, 76658, 38279, 9898],
    step: 8,
    effect: 'wipe-right'
  },
  {
    key: 'principal-volatile',
    src: 'slide',
    rect: [64.18, 69.67, 13.38, 19.21],
    crop: [65755, 72886, 19843, 6457],
    step: 11,
    effect: 'split-out'
  },
  {
    key: 'principal-insurance',
    src: 'slide',
    rect: [82.5, 68.5, 9, 13.5],
    crop: [85500, 72000, 6500, 16000],
    step: 14,
    effect: 'wipe-up'
  },
  {
    key: 'keep',
    kind: 'keep',
    rect: [76.5, 82.5, 21, 14],
    messages: ['its-capital-protected', 'its-only-rise'],
    size: 2.3,
    enSize: 1.6,
    step: 14,
    effect: 'wipe-up'
  },
  {
    key: 'principal-down-property',
    src: 'arrow-down',
    rect: [15.73, 71.33, 9.31, 16.54],
    step: 5,
    effect: 'wipe-down'
  },
  { key: 'principal-down-stocks', src: 'arrow-down', rect: [32.39, 71.44, 9.31, 16.54], step: 5, effect: 'wipe-down' },
  {
    key: 'question-rent',
    src: 'slide',
    rect: [23.0, 54.6, 4.7, 8.7],
    crop: QUESTION_CROP,
    step: 5,
    effect: 'wipe-down'
  },
  {
    key: 'question-dividend',
    src: 'slide',
    rect: [39.9, 54.4, 4.7, 8.7],
    crop: QUESTION_CROP,
    step: 5,
    effect: 'wipe-down'
  },
  {
    key: 'question-interest',
    src: 'slide',
    rect: [56.9, 54.6, 4.7, 8.7],
    crop: QUESTION_CROP,
    step: 8,
    effect: 'wipe-right'
  }
];

export const LAST_STEP = Math.max(...PARTS.map(part => part.step));

/** Places the whole picture inside the part's box so only the `srcRect` window shows. */
const cropStyle = (crop: ImagePart['crop']): React.CSSProperties => {
  if (!crop) return { left: 0, top: 0, width: '100%', height: '100%' };
  const [l, t, r, b] = crop.map(v => v / 100000);
  const w = 1 - l - r;
  const h = 1 - t - b;
  return { left: `${(-l / w) * 100}%`, top: `${(-t / h) * 100}%`, width: `${100 / w}%`, height: `${100 / h}%` };
};

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }) => string;
}

const SlidePartView = ({ part, shown, intl }: { part: SlidePart; shown: boolean; intl: Intl }) => {
  const [left, top, width, height] = part.rect;
  const style: React.CSSProperties & { '--ascend-from'?: string } = {
    left: `${left}%`,
    top: `${top}%`,
    width: `${width}%`,
    height: `${height}%`
  };
  // pptx Ascend starts 0.1 slide-heights low; translateY % is of the part's own height.
  if (part.effect === 'ascend') style['--ascend-from'] = `${(10 / height) * 100}%`;

  const className = [
    'its-part',
    isText(part) && `its-text its-${part.kind}`,
    part.effect && `its-${part.effect}`,
    (part.step === 0 || shown) && 'is-shown'
  ]
    .filter(Boolean)
    .join(' ');

  if (isText(part)) {
    const size = intl.locale === 'en' && part.enSize ? part.enSize : part.size;
    const lines = part.messages ? part.messages.map(id => intl.formatMessage({ id })) : [part.literal];
    return (
      <div className={className} style={{ ...style, fontSize: `${size}em` }} data-part={part.key}>
        <span>
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </span>
      </div>
    );
  }

  return (
    <div className={className} style={style} data-part={part.key}>
      <img src={`${IMG_DIR}/${part.src}.webp`} alt="" draggable={false} style={cropStyle(part.crop)} />
    </div>
  );
};

/** Text sizes are in 1/100 of the slide width, so they scale with the pictures. */
const useSlideUnit = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [unit, setUnit] = useState<number>();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setUnit(el.clientWidth / 100);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, unit };
};

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

interface InvestmentToolsSlideProps {
  intl: Intl;
}

const InvestmentToolsSlide = ({ intl }: InvestmentToolsSlideProps) => {
  const [step, setStep] = useState(0);
  const { ref, unit } = useSlideUnit();

  const next = useCallback(() => setStep(s => Math.min(s + 1, LAST_STEP)), []);
  const prev = useCallback(() => setStep(s => Math.max(s - 1, 0)), []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (['ArrowRight', 'ArrowDown', 'PageDown', ' ', 'Enter'].includes(event.key)) {
      event.preventDefault();
      next();
    } else if (['ArrowLeft', 'ArrowUp', 'PageUp', 'Backspace'].includes(event.key)) {
      event.preventDefault();
      prev();
    }
  };

  const t = (id: string) => intl.formatMessage({ id });

  return (
    <div className="investment-tools-slide" lang={HTML_LANG[intl.locale] || intl.locale}>
      <div
        ref={ref}
        className={`its-stage${step < LAST_STEP ? ' is-advancing' : ''}`}
        style={unit ? { fontSize: unit } : undefined}
        role="button"
        tabIndex={0}
        aria-label={t('its-stage-label')}
        onClick={next}
        onKeyDown={onKeyDown}
      >
        {PARTS.map(part => (
          <SlidePartView key={part.key} part={part} shown={step >= part.step} intl={intl} />
        ))}
      </div>
      <div className="its-progress" aria-hidden="true">
        <span style={{ width: `${(step / LAST_STEP) * 100}%` }} />
      </div>

      <div className="its-controls">
        <IconButton onClick={() => setStep(0)} disabled={step === 0} aria-label={t('its-replay')} size="small">
          <ReplayRoundedIcon />
        </IconButton>
        <IconButton onClick={prev} disabled={step === 0} aria-label={t('its-previous')}>
          <NavigateBeforeRoundedIcon />
        </IconButton>
        <Typography className="its-counter" variant="body2" aria-live="polite">
          {step === 0 ? t('its-start-hint') : `${step} / ${LAST_STEP}`}
        </Typography>
        <IconButton
          className={`its-next${step === 0 ? ' is-idle' : ''}`}
          onClick={next}
          disabled={step === LAST_STEP}
          aria-label={t('its-next')}
        >
          <NavigateNextRoundedIcon />
        </IconButton>
        <Button className="its-show-all" onClick={() => setStep(LAST_STEP)} disabled={step === LAST_STEP} size="small">
          {t('its-show-all')}
        </Button>
      </div>

      <div className="its-banner">
        <LightbulbRoundedIcon aria-hidden="true" />
        <p>
          {t('its-banner-1')}
          <br />
          {t('its-banner-2')}
        </p>
      </div>

      <div className="its-disclaimer">
        <p>
          {t('its-disclaimer-1')}
          <br />
          {t('its-disclaimer-2')}
        </p>
        <img src="img/logo.png" alt="PortfoPlus" />
      </div>
    </div>
  );
};

export default InvestmentToolsSlide;
